import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { State } from '@store/index';
import { Observable, Subject, takeUntil } from 'rxjs';
import * as TablesPlanSelectors from '@store/selectors/tables-plan.selectors';
import { Section, TablesMode, WorkingDate } from '@model/tables-plan';
import { TablesPlanActions } from '@store/actions/tables-plan.actions';
import { ModalDialogComponent } from 'src/app/modal-dialog/modal-dialog.component';
import { Reservation, ReservationStatus, Table } from '@model/reservation';
import { Common } from 'src/app/lib/common-methods';
import { Employee } from '@model/employee';
import * as EmployeeSelectors from '@store/selectors/employee.selectors';
import { TablesPlanService } from '../tables-plan.service';
import { Toast } from 'bootstrap';
declare const bootstrap: any;

@Component({
  selector: 'app-topbar',
  templateUrl: './topbar.component.html',
  styleUrl: './topbar.component.scss'
})
export class TopbarComponent implements OnInit, OnDestroy {

  constructor(
    private tablesPlanService: TablesPlanService,
    private store: Store<State>,
  ) {

  }

  destroy$: Subject<boolean> = new Subject<boolean>();

  @ViewChild('selectSectionModal') selectSectionModal!: ModalDialogComponent;
  @ViewChild('addEditSectionModal') addEditSectionModal!: ModalDialogComponent;
  @ViewChild('confirmDeleteSectionModal') confirmDeleteSectionModal!: ModalDialogComponent;

  editMode$: Observable<boolean> = this.store.pipe(select(TablesPlanSelectors.selectEditMode));
  editMode: boolean = false;
  tablesMode$: Observable<TablesMode> = this.store.pipe(select(TablesPlanSelectors.selectTablesMode));
  tablesMode: TablesMode = 'Tables';
  addJoinedTablesMode$: Observable<boolean> = this.store.pipe(select(TablesPlanSelectors.selectAddJoinedTablesMode));
  addJoinedTablesMode: boolean = false;
  workingDate$: Observable<WorkingDate | null | undefined> = this.store.pipe(select(TablesPlanSelectors.selectWorkingDate));
  workingDate: WorkingDate | null | undefined = null;
  workingDateSections$: Observable<Section[] | null> = this.store.pipe(select(TablesPlanSelectors.selectWorkingDateSections));
  workingDateSections: Section[] | null = null;
  selectedSection$: Observable<Section | null | undefined> = this.store.pipe(select(TablesPlanSelectors.selectSelectedSection));
  selectedSection: Section | null | undefined = null;
  filteredReservations$: Observable<Reservation[]> = this.store.pipe(select(TablesPlanSelectors.selectReservationsFilteredBySelectedTables));
  filteredReservations: Reservation[] = [];
  reservationsFilteredByShiftAndSection$: Observable<Reservation[]> = this.store.pipe(select(TablesPlanSelectors.selectReservationsFilteredByShiftAndSection));
  reservationsFilteredByShiftAndSection: Reservation[] = [];
  sidePanelCollapsed$: Observable<boolean> = this.store.pipe(select(TablesPlanSelectors.selectSidePanelCollapsed));
  sidePanelCollapsed: boolean = false;
  loggedInemployee$: Observable<Employee> = this.store.pipe(select(EmployeeSelectors.selectLoggedInemployee));
  loggedInemployee: Employee | null = null;
  editingTable$: Observable<{ table: Table, isNew: boolean } | null> = this.store.pipe(select(TablesPlanSelectors.selectEditingTable));
  editingTable: { table: Table, isNew: boolean } | null = null;
  assignCategoriesToTablesMode$: Observable<boolean> = this.store.pipe(select(TablesPlanSelectors.selectAssignCategoriesToTablesMode));
  assignCategoriesToTablesMode: boolean = false;
  isOnExcludeFromOnlineChannelsMode$: Observable<boolean> = this.store.pipe(select(TablesPlanSelectors.selectIsOnExcludeFromOnlineChannelsMode));
  isOnExcludeFromOnlineChannelsMode: boolean = false;

  editingSection: Section | null = null;
  isNewlyCreatedSection: boolean = false;
  validationMessage: string | null = '';

  totalReservationsFilter = (reservation: Reservation) => {
    const status = reservation.reservationDetails.reservationsStatus;
    return status != 'Waiting' && status != 'Pending' && status != 'Cancelled' && status != 'NotShown' && status != 'Rejected';
  }

  upcomingReservationsFilter = (reservation: Reservation) => {
    const status = reservation.reservationDetails.reservationsStatus;
    return status === 'Confirmed' || status === 'LateBooking' || status === 'CheckedIn(Partial)';
  }

  // get totalReservationsNumber(): number {
  //   return this.reservationsFilteredByShiftAndSection
  //     .filter(this.totalReservationsFilter).length;
  // }
  // get upcomingReservationsNumber(): number {
  //   return this.reservationsFilteredByShiftAndSection
  //     .filter(this.upcomingReservationsFilter).length;
  // }
  // get totalReservationsCoversNumber(): number {
  //   return this.reservationsFilteredByShiftAndSection
  //     .filter(this.totalReservationsFilter)
  //     .map(r => r.reservationDetails.covers)
  //     .reduce((a, b) => a + b, 0);
  // }
  // get upcomingReservationsCoversNumber(): number {
  //   return this.reservationsFilteredByShiftAndSection
  //     .filter(this.upcomingReservationsFilter)
  //     .map(r => r.reservationDetails.covers)
  //     .reduce((a, b) => a + b, 0);
  // }

  ngOnInit(): void {

    this.editMode$.pipe(takeUntil(this.destroy$)).subscribe((editMode: boolean) => {
      this.editMode = editMode;

      if (editMode && (this.workingDateSections ?? [])?.length <= 0) {
        // if the editing working date is regular, the working date will get a new workingDateId, wait to set the new id to the editing section
        setTimeout(() => {
          this.addSection();
        }, 500);
      }
    });

    this.workingDate$.pipe(takeUntil(this.destroy$)).subscribe((workingDate: WorkingDate | null | undefined) => {
      this.workingDate = workingDate;
    });

    this.workingDateSections$.pipe(takeUntil(this.destroy$)).subscribe((workingDateSections) => {
      this.workingDateSections = workingDateSections;
    });

    this.selectedSection$.pipe(takeUntil(this.destroy$)).subscribe((selectedSection: Section | null | undefined) => {
      this.selectedSection = selectedSection;
    });

    this.filteredReservations$.pipe(takeUntil(this.destroy$)).subscribe((filteredReservations: Reservation[]) => {
      this.filteredReservations = filteredReservations;
    });

    this.sidePanelCollapsed$.pipe(takeUntil(this.destroy$)).subscribe((sidePanelCollapsed: boolean) => {
      this.sidePanelCollapsed = sidePanelCollapsed;
    });

    this.reservationsFilteredByShiftAndSection$.pipe(takeUntil(this.destroy$)).subscribe((reservationsFilteredByShiftAndSection) => {
      this.reservationsFilteredByShiftAndSection = reservationsFilteredByShiftAndSection;
    });

    this.loggedInemployee$.pipe(takeUntil(this.destroy$)).subscribe((loggedInemployee) => {
      this.loggedInemployee = loggedInemployee;
    });

    this.tablesMode$.pipe(takeUntil(this.destroy$)).subscribe((tablesMode) => {
      this.tablesMode = tablesMode;
    });

    this.editingTable$.pipe(takeUntil(this.destroy$)).subscribe((editingTable) => {
      this.editingTable = editingTable;
    });

    this.addJoinedTablesMode$.pipe(takeUntil(this.destroy$)).subscribe((addJoinedTablesMode: boolean) => {
      this.addJoinedTablesMode = addJoinedTablesMode;
    });

    this.assignCategoriesToTablesMode$.pipe(takeUntil(this.destroy$)).subscribe((assignCategoriesToTablesMode: boolean) => {
      this.assignCategoriesToTablesMode = assignCategoriesToTablesMode;
    });

    this.isOnExcludeFromOnlineChannelsMode$.pipe(takeUntil(this.destroy$)).subscribe((isOnExcludeFromOnlineChannelsMode: boolean) => {
      this.isOnExcludeFromOnlineChannelsMode = isOnExcludeFromOnlineChannelsMode;
    });

  }

  showSelectSectionModal() {
    if (this.editMode && (this.editingTable != null || this.addJoinedTablesMode)) {
      return;
    }
    this.selectSectionModal.show();
  }

  hideSelectSectionModal() {
    this.selectSectionModal.hide();
  }

  showAddEditSelectSectionModal() {
    this.addEditSectionModal.show();
  }

  hideAddEditSelectSectionModal() {
    this.addEditSectionModal.hide();
  }

  setSection(section: Section) {
    this.store.dispatch({ type: TablesPlanActions.setSection.type, section });
    this.hideSelectSectionModal();
  }

  toggleCollapseSidePanel() {
    const collapsed = !this.sidePanelCollapsed;
    this.store.dispatch({ type: TablesPlanActions.setSidePanelCollapsed.type, collapsed });
  }

  addSection() {

    this.isNewlyCreatedSection = true;

    let sectionOrder = 1;
    if (this.workingDateSections != null && this.workingDateSections.length > 0) {
      sectionOrder = (this.workingDateSections.sort(Common.sectionOrderComperator)[this.workingDateSections.length - 1]?.sectionOrder ?? 0) + 1;
    }

    this.editingSection = {
      sectionId: Common.generateUuidBase64Encoded(),
      sectionCapacity: 0,
      sectionName: '',
      sectionOrder,
      sectionTables: null,
      workingDateId: this.workingDate?.workingDateId as string,
      excludeOnline: false,
    };
    this.hideSelectSectionModal();
    this.showAddEditSelectSectionModal();
  }

  setSectionOrder(event: number) {
    if (this.editingSection == null) {
      return;
    }
    this.editingSection.sectionOrder = event;
    this.validationMessage = '';
  }

  setSectionName(event: string) {
    if (this.editingSection == null) {
      return;
    }
    this.editingSection.sectionName = event;
    this.validationMessage = '';
  }

  editSection(section: Section) {
    this.isNewlyCreatedSection = false;
    this.editingSection = JSON.parse(JSON.stringify(section)); // make it mutable
    this.selectSectionModal.hide();
    this.showAddEditSelectSectionModal();
  }

  showDeleteSectionConfirmation(section: Section) {
    this.editingSection = section;
    this.selectSectionModal.hide();
    this.confirmDeleteSectionModal.show();
  }

  cancelEditSection() {
    this.editingSection = null;
    this.addEditSectionModal.hide();
    this.selectSectionModal.show();
  }

  applySectionChanges() {

    const validationError = this.tablesPlanService.isSectionValid(this.editingSection as Section);

    if (!Common.isNullOrEmpty(validationError)) {
      // show validation message
      this.validationMessage = validationError;
      return;
    }

    if (this.isNewlyCreatedSection) {
      this.store.dispatch({ type: TablesPlanActions.createSection.type, section: this.editingSection, modifiedBy: this.loggedInemployee?.surname });
    } else {
      this.store.dispatch({ type: TablesPlanActions.updateSection.type, section: this.editingSection, modifiedBy: this.loggedInemployee?.surname });
    }

    this.cancelEditSection();
  }

  cancelDeleteSection(ok: boolean) {

    if (ok) {
      this.store.dispatch({ type: TablesPlanActions.deleteSection.type, section: this.editingSection, modifiedBy: this.loggedInemployee?.surname });
    }

    this.editingSection = null;
    this.confirmDeleteSectionModal.hide();
    this.selectSectionModal.show();
  }

  setTablesMode(mode: TablesMode) {
    this.store.dispatch({ type: TablesPlanActions.setTablesMode.type, mode });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }
}
